import { useEffect, useMemo, useState } from "react";
import { apiGet } from "../api/http";
import { parseApiError } from "../utils/apiErrors";
import { useToast } from "../context/ToastContext";
import { formatDateTime } from "../utils/dateTime";

function flattenMetricGroup(group, type) {
  if (!group || typeof group !== "object") return [];
  const rows = [];
  for (const [name, metric] of Object.entries(group)) {
    const values = Array.isArray(metric?.values) ? metric.values : [];
    if (values.length === 0) {
      rows.push({ type, name, labels: "-", value: "-" });
      continue;
    }
    for (const entry of values) {
      const labels = entry?.labels && Object.keys(entry.labels).length > 0
        ? JSON.stringify(entry.labels)
        : "-";
      rows.push({
        type,
        name,
        labels,
        value: entry?.value ?? "-",
      });
    }
  }
  return rows;
}

export default function Metrics({ inline }) {
  const { showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    apiGet("/api/metrics")
      .then(({ data }) => {
        if (!mounted) return;
        setPayload(data);
      })
      .catch((err) => {
        if (!mounted) return;
        showError(parseApiError(err, "Failed to load metrics"));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [showError]);

  const rows = useMemo(() => {
    if (!payload) return [];
    return [
      ...flattenMetricGroup(payload.counters, "counter"),
      ...flattenMetricGroup(payload.gauges, "gauge"),
      ...flattenMetricGroup(payload.histograms, "histogram"),
      ...flattenMetricGroup(payload.summaries, "summary"),
    ];
  }, [payload]);

  const totals = payload?.totals || {};

  return (
    <div className={inline ? "" : "page page-dashboard"}>
      {!inline && (
        <>
          <h1 className="page-title">Metrics</h1>
          <p className="dashboard-lead">
            Live backend telemetry from the JSON metrics endpoint, including product counters and runtime stats.
          </p>
        </>
      )}

      {loading ? <div className="loading">Loading metrics...</div> : null}

      {!loading ? (
        <>
          <div className="dashboard-kpis">
            <div className="card dashboard-kpi">
              <div className="dashboard-kpi-label">SEO Audit Runs</div>
              <div className="dashboard-kpi-value">{totals.auditRuns ?? "--"}</div>
            </div>
            <div className="card dashboard-kpi">
              <div className="dashboard-kpi-label">Widget Hits</div>
              <div className="dashboard-kpi-value">{totals.widgetHits ?? "--"}</div>
            </div>
            <div className="card dashboard-kpi">
              <div className="dashboard-kpi-label">New Signups</div>
              <div className="dashboard-kpi-value">{totals.newSignups ?? "--"}</div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="dashboard-kpi-label">Generated At</div>
            <div>{payload?.generatedAt ? formatDateTime(payload.generatedAt) : "-"}</div>
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Name</th>
                  <th>Labels</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4}>No metrics returned.</td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr key={`${row.type}-${row.name}-${idx}`}>
                      <td>{row.type}</td>
                      <td>{row.name}</td>
                      <td>{row.labels}</td>
                      <td>{String(row.value)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
